'use client';

import { useTranslation } from 'next-i18next';
import { ApplicantStatus } from '@type/company-job';
import { Tag } from './Tag';

const TAGS: {
  label: string;
  href: (jobSlug: string) => string;
  countKey?: Extract<keyof ApiSchema<'MyJobCard'>, `number${string}`>;
  className: string;
  status?: number;
  isRecommended?: boolean;
}[] = [
  {
    label: 'my-jobs.card.tags.new',
    countKey: 'numberOfNewApplicants',
    href: (jobSlug) => `/job/${jobSlug}/applicants?status=${ApplicantStatus.NEW}`,
    className: 'text-primary hover:bg-primary/10',
    status: ApplicantStatus.NEW,
  },
  {
    label: 'my-jobs.card.tags.shortlisted',
    countKey: 'numberOfShortlistedApplicants',
    href: (jobSlug) => `/job/${jobSlug}/applicants?status=${ApplicantStatus.SHORTLIST}`,
    className: 'text-secondary hover:bg-secondary/10',
    status: ApplicantStatus.SHORTLIST,
  },
  {
    label: 'my-jobs.card.tags.interview',
    countKey: 'numberOfInterviewApplicants',
    href: (jobSlug) => `/job/${jobSlug}/applicants?status=${ApplicantStatus.INTERVIEW}`,
    className: 'text-pink hover:bg-pink/10',
    status: ApplicantStatus.INTERVIEW,
  },
  {
    label: 'my-jobs.card.tags.recommended',
    href: (jobSlug) => `/job/${jobSlug}/applicants/flow`,
    className: 'hover:bg-[#F0F2F4] dark:hover:text-dark',
    isRecommended: true,
  },
];

interface TagsProps {
  job: ApiSchema<'MyJobCard'>;
  /** When true, all tags use text-ink color (for compact sidebar cards) */
  useInkColor?: boolean;
  /** When true, the Recommended tag is not rendered (e.g. compact sidebar cards) */
  hideRecommended?: boolean;
  /**
   * When provided, clicking a tag calls this handler instead of navigating.
   * Receives the ApplicantStatus value for the clicked tag.
   */
  onTagClick?: (status: number) => void;
  /**
   * Optional override for the tag href. Defaults to /job/[slug]/applicants?status=XX.
   * Use this in Mission Control context to produce /[locale]/mc/job/[slug]/applicants?status=XX.
   */
  buildHref?: (jobSlug: string, status: number) => string;
}

export const Tags = (props: TagsProps) => {
  // Destructure props
  const {
    job, useInkColor, hideRecommended, onTagClick, buildHref,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  return (
    <>
      {TAGS.map((tag) => {
        if (hideRecommended && tag.isRecommended) return null;

        // Recommended tag has no status — always use its own href, never forward to buildHref/onTagClick
        const href = (!tag.isRecommended && buildHref && tag.status !== undefined)
          ? buildHref(job.slug!, tag.status)
          : tag.href(job.slug!);

        const handleClick = (!tag.isRecommended && onTagClick && tag.status !== undefined)
          ? () => onTagClick(tag.status!)
          : undefined;

        return (
          <Tag
            key={tag.label}
            label={t(tag.label)}
            href={href}
            count={tag.countKey && job[tag.countKey]}
            className={useInkColor ? 'text-ink hover:bg-ink/10' : tag.className}
            onClick={handleClick}
          />
        );
      })}
    </>
  );
};
